import React from "react";
import {
  Typography,
  Box,
  Chip,
  Button,
  Alert,
} from "@mui/material";
import {
  Business,
  Person,
  AdminPanelSettings,
  CheckCircle,
  RemoveCircleOutline,
  Refresh,
  VerifiedUser,
} from "@mui/icons-material";
import { useAuth } from "../context/EnterpriseAuthContext";
import { PageContainer, PageHeader, SectionCard, IconTile } from "./pagekit";

/**
 * Human label for a membership role.
 * @param {string} role - Role id.
 * @returns {string} Label.
 */
function roleLabel(role) {
  const text = String(role || "member").replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Chip colour for a role.
 * @param {string} role - Role id.
 * @returns {'primary'|'info'|'default'} Colour.
 */
function roleColor(role) {
  if (role === "account_admin") return "primary";
  if (role === "template_creator") return "info";
  return "default";
}

/**
 * Definition-list row.
 * @param {object} props
 * @param {string} props.label - Term.
 * @param {React.ReactNode} props.children - Value.
 * @returns {JSX.Element}
 */
function Row({ label, children }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "140px minmax(0, 1fr)" }, gap: { xs: 0.25, sm: 1.5 } }}>
      <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: "anywhere" }}>{children}</Typography>
    </Box>
  );
}

/**
 * Account information: the signed-in user, the active workspace, role and
 * permissions, and every workspace the user belongs to (with switching).
 * @returns {JSX.Element}
 */
function AccountInfo() {
  const { state, actions } = useAuth();

  if (!state.isAuthenticated) {
    return (
      <PageContainer width="narrow">
        <Alert severity="info">Please sign in to view account information.</Alert>
      </PageContainer>
    );
  }

  const permissions = [
    { label: "Account admin", allowed: actions.isAccountAdmin() },
    { label: "Manage templates", allowed: actions.canManageTemplates() },
    { label: "Manage users", allowed: actions.canManageUsers() },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Account"
        description="Your profile, your role in this workspace and the workspaces you belong to."
        actions={
          <Button variant="outlined" startIcon={<Refresh />} onClick={actions.refreshAccountData}>
            Refresh
          </Button>
        }
      />

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, alignItems: "start" }}>
        <SectionCard title="Profile" icon={<Person />}>
          <Box component="dl" sx={{ m: 0, display: "grid", gap: 1.25 }}>
            <Row label="Username">{state.user?.username}</Row>
            <Row label="Email">{state.user?.email}</Row>
            <Row label="User ID">
              <Box component="span" sx={{ fontFamily: "var(--q-font-mono)", fontSize: "0.78rem" }}>{state.user?.id}</Box>
            </Row>
          </Box>
        </SectionCard>

        {state.activeAccount && (
          <SectionCard title="Active workspace" icon={<Business />}>
            <Box component="dl" sx={{ m: 0, display: "grid", gap: 1.25 }}>
              <Row label="Name">{state.activeAccount.name}</Row>
              <Row label="Domain">{state.activeAccount.domain || "None"}</Row>
              <Row label="Description">{state.activeAccount.description || "None"}</Row>
              <Row label="Account ID">
                <Box component="span" sx={{ fontFamily: "var(--q-font-mono)", fontSize: "0.78rem" }}>{state.activeAccount.id}</Box>
              </Row>
            </Box>
          </SectionCard>
        )}
      </Box>

      <SectionCard
        title="Role and permissions"
        icon={<VerifiedUser />}
        actions={
          state.currentMembership ? (
            <>
              <Chip
                label={roleLabel(state.currentMembership.role)}
                color={roleColor(state.currentMembership.role)}
                icon={state.currentMembership.role === "account_admin" ? <AdminPanelSettings /> : <Person />}
              />
              <Chip label={`Status: ${state.currentMembership.status}`} variant="outlined" />
            </>
          ) : null
        }
      >
        <Box component="ul" sx={{ m: 0, p: 0, display: "grid", gap: 1, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" } }}>
          {permissions.map((p) => (
            <Box
              component="li"
              key={p.label}
              sx={{ listStyle: "none", display: "flex", alignItems: "center", gap: 1, p: 1.25, border: 1, borderColor: "divider", borderRadius: "var(--q-radius-lg)" }}
            >
              {p.allowed ? (
                <CheckCircle fontSize="small" sx={{ color: "success.main" }} />
              ) : (
                <RemoveCircleOutline fontSize="small" sx={{ color: "text.disabled" }} />
              )}
              <Typography variant="body2" sx={{ flex: 1 }}>{p.label}</Typography>
              <Typography variant="caption" color="text.secondary">{p.allowed ? "Yes" : "No"}</Typography>
            </Box>
          ))}
        </Box>
      </SectionCard>

      {state.memberships.length > 0 && (
        <SectionCard flush title={`Your workspaces (${state.memberships.length})`}>
          <Box component="ul" sx={{ m: 0, p: 0 }}>
            {state.memberships.map((membership) => {
              const active = membership.account_id === state.activeAccount?.id;
              return (
                <Box
                  component="li"
                  key={membership.id}
                  sx={{
                    listStyle: "none",
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1.5,
                    px: { xs: 1.5, sm: 2 },
                    py: 1.25,
                    bgcolor: active ? "var(--q-accent-tint)" : "transparent",
                    borderBottom: 1,
                    borderColor: "var(--q-row-divider)",
                    "&:last-of-type": { borderBottom: 0 },
                  }}
                >
                  <IconTile tone={active ? "accent" : "neutral"} size={32}><Business /></IconTile>
                  <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                      {membership.accounts?.name || "Unknown workspace"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {membership.accounts?.domain || "No domain"}
                    </Typography>
                  </Box>
                  <Chip size="small" label={roleLabel(membership.role)} color={roleColor(membership.role)} />
                  {active ? (
                    <Chip size="small" label="Active" color="primary" variant="outlined" />
                  ) : (
                    <Button size="small" variant="outlined" onClick={() => actions.switchAccount(membership.account_id)}>
                      Switch
                    </Button>
                  )}
                </Box>
              );
            })}
          </Box>
        </SectionCard>
      )}
    </PageContainer>
  );
}

export default AccountInfo;
